import { Button, Dialog, DialogActions } from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ParticipantType } from "../api/graphql.ts";
import { useAuthContext } from "../AuthProvider";
import { EndVoiceCallDocument } from "../Call/EndVoiceCall.generated";
import { GetCallInfoDocument } from "../Call/GetCallInfo.generated.ts";
import { useServerEvent } from "../lib/serverEventsProvider";
import { useCurrentUser } from "../SessionBoundary";

function AuthenticatedVoiceCallListener() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [modalShown, setModalShown] = useState(false);
  const [latestCallId, setLatestCallId] = useState<string>();

  const { data: callData } = useQuery(GetCallInfoDocument, {
    variables: {
      callId: latestCallId || "",
    },
    skip: !latestCallId,
  });

  const [decline] = useMutation(EndVoiceCallDocument, {
    variables: {
      input: {
        callId: latestCallId || "",
      },
    },
  });

  useServerEvent("newVoiceCall", ({ callId }) => {
    setLatestCallId(callId);
    setModalShown(true);
  });

  useServerEvent("voiceCallEnded", ({ callId }) => {
    if (callId === latestCallId) {
      setModalShown(false);
      setLatestCallId("");
    }
  });

  const currentParticipantId = callData?.call.currentParticipant.id;
  const caller = callData?.call.call.participants.find(
    (p) => p.id !== currentParticipantId
  );
  const callerName =
    caller?.type === ParticipantType.Doc
      ? callData?.call.call.facility.name
      : caller?.fullName;

  if (modalShown && latestCallId && caller) {
    return (
      <Dialog
        title={
          callerName
            ? t("Incoming voice call from {{name}}", {
                name: callerName,
              })
            : t("Incoming voice call from a facility")
        }
        onClose={() => {
          decline();
          setModalShown(false);
        }}
      >
        <DialogActions>
          <Button
            onClick={() => {
              decline();
              setModalShown(false);
            }}
          >
            {t("Decline")}
          </Button>
          <Button onClick={() => navigate(`/voice-call/${latestCallId}`)}>
            {t("Answer")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
}

export default function VoiceCallListener() {
  const { isLoggedIn } = useAuthContext();
  const user = useCurrentUser();
  return isLoggedIn && user.__typename === "Visitor" ? (
    <AuthenticatedVoiceCallListener />
  ) : null;
}
